import { invoke, isTauri } from '@tauri-apps/api/core';

export interface SaveImageResult {
  success: boolean;
  path?: string;
  message: string;
}

function isTauriEnvironment(): boolean {
  return isTauri();
}

function extractFilenameFromUrl(url: string): string {
  try {
    const decodedUrl = decodeURIComponent(url);
    
    const urlObj = new URL(decodedUrl);
    const pathname = urlObj.pathname;
    
    const filename = pathname.split('/').pop() || '';
    
    if (filename && filename.includes('.')) {
      const cleanFilename = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
      
      if (cleanFilename.length > 100) {
        const ext = cleanFilename.split('.').pop() || 'png';
        return `image_${Date.now()}.${ext}`;
      }
      
      return cleanFilename;
    }
  } catch (e) {
    console.log('[ImageSave] URL 解析失败，使用备用方案');
  }
  
  const parts = url.split('/');
  const lastPart = parts[parts.length - 1];
  const decoded = decodeURIComponent(lastPart);
  
  if (decoded && decoded.includes('.')) {
    const clean = decoded.replace(/[^a-zA-Z0-9._-]/g, '_');
    return clean.length > 100 ? `image_${Date.now()}.png` : clean;
  }
  
  return '';
}

function generateSmartFilename(url?: string, mimeType?: string): string {
  if (url) {
    const extracted = extractFilenameFromUrl(url);
    if (extracted) {
      return extracted;
    }
  }
  
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
  
  let ext = 'png';
  if (mimeType) {
    ext = getExtensionFromMime(mimeType);
  } else if (url) {
    const urlExt = url.split('.').pop()?.toLowerCase();
    if (urlExt && ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp'].includes(urlExt)) {
      ext = urlExt === 'jpg' ? 'jpg' : urlExt;
    }
  }
  
  return `image_${dateStr}_${timeStr}.${ext}`;
}

function getPlatform(): 'desktop' | 'mobile' | 'web' {
  if (!isTauriEnvironment()) {
    return 'web';
  }
  
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('android') || userAgent.includes('iphone') || userAgent.includes('ipad')) {
    return 'mobile';
  }
  return 'desktop';
}

export async function saveImage(
  imageSource: string | Blob,
  filename?: string
): Promise<SaveImageResult> {
  const platform = getPlatform();
  
  console.log(`[ImageSave] 平台: ${platform}, 图片源类型: ${typeof imageSource}`);
  
  if (platform === 'web') {
    return saveImageWeb(imageSource, filename);
  }
  
  if (platform === 'mobile') {
    return saveImageMobile(imageSource, filename);
  }
  
  return saveImageDesktop(imageSource, filename);
}

async function saveImageDesktop(
  imageSource: string | Blob,
  filename?: string
): Promise<SaveImageResult> {
  try {
    let imageData: Uint8Array;
    let defaultFilename: string;
    
    if (typeof imageSource === 'string') {
      if (imageSource.startsWith('data:')) {
        const base64Data = imageSource.split(',')[1];
        imageData = base64ToUint8Array(base64Data);
        
        const mimeMatch = imageSource.match(/data:([^;]+);/);
        const mimeType = mimeMatch ? mimeMatch[1] : undefined;
        defaultFilename = filename || generateSmartFilename(undefined, mimeType);
      } else if (imageSource.startsWith('asset://') || imageSource.startsWith('file://')) {
        defaultFilename = filename || generateSmartFilename(imageSource);
        const result: SaveImageResult = await invoke('save_image_from_path', {
          sourcePath: decodeURIComponent(imageSource.replace('asset://localhost', '').replace('file://', '')),
          defaultFilename: defaultFilename,
        });
        return result;
      } else {
        const response = await fetch(imageSource);
        const blob = await response.blob();
        imageData = new Uint8Array(await blob.arrayBuffer());
        defaultFilename = filename || generateSmartFilename(imageSource, blob.type);
      }
    } else {
      imageData = new Uint8Array(await imageSource.arrayBuffer());
      defaultFilename = filename || generateSmartFilename(undefined, imageSource.type);
    }
    
    const result: SaveImageResult = await invoke('save_image_to_file', {
      imageData: Array.from(imageData),
      defaultFilename: defaultFilename,
    });
    
    return result;
  } catch (error) {
    console.error('[ImageSave] 桌面端保存失败:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '保存图片失败',
    };
  }
}

async function saveImageMobile(
  imageSource: string | Blob,
  filename?: string
): Promise<SaveImageResult> {
  try {
    let imageData: Uint8Array;
    let defaultFilename: string;
    
    if (typeof imageSource === 'string') {
      if (imageSource.startsWith('data:')) {
        const base64Data = imageSource.split(',')[1];
        imageData = base64ToUint8Array(base64Data);
        const mimeMatch = imageSource.match(/data:([^;]+);/);
        defaultFilename = filename || generateSmartFilename(undefined, mimeMatch ? mimeMatch[1] : undefined);
      } else {
        const response = await fetch(imageSource);
        const blob = await response.blob();
        imageData = new Uint8Array(await blob.arrayBuffer());
        defaultFilename = filename || generateSmartFilename(imageSource, blob.type);
      }
    } else {
      imageData = new Uint8Array(await imageSource.arrayBuffer());
      defaultFilename = filename || generateSmartFilename(undefined, imageSource.type);
    }
    
    // ★ Rust save_image_to_downloads 返回 Result<String>（成功 = 路径字符串），
    // 非 {success, path} 对象——此处兼容两种返回形态
    const raw: unknown = await invoke('save_image_to_downloads', {
      imageData: Array.from(imageData),
      filename: defaultFilename,
    });

    const result: SaveImageResult =
      typeof raw === 'string' || typeof raw === 'object'
        ? {
            success: true,
            path: typeof raw === 'string' ? raw : (raw as any)?.path || String(raw),
            message: '保存成功',
          }
        : { success: false, message: '保存失败' };

    if (result.success) {
      console.log('[ImageSave] 图片已保存到:', result.path);
      // ★ 不用 alert（WebView 中 alert 阻塞 JS 且不显示），改轻量提示
      try {
        const { toast } = await import('@components/ui/toast');
        toast.success(`图片已保存到: ${result.path}`);
      } catch {
        // toast 不可用时静默
      }
    }

    return result;
  } catch (error) {
    console.error('[ImageSave] 移动端保存失败:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : '保存图片失败',
    };
  }
}

function saveImageWeb(
  imageSource: string | Blob,
  filename?: string
): Promise<SaveImageResult> {
  return new Promise((resolve) => {
    try {
      const link = document.createElement('a');
      
      if (typeof imageSource === 'string') {
        link.href = imageSource;
      } else {
        link.href = URL.createObjectURL(imageSource);
      }
      
      link.download = filename || `image_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      if (typeof imageSource !== 'string') {
        URL.revokeObjectURL(link.href);
      }
      
      resolve({
        success: true,
        message: '图片下载已开始',
      });
    } catch (error) {
      resolve({
        success: false,
        message: error instanceof Error ? error.message : '下载失败',
      });
    }
  });
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function getExtensionFromMime(mime: string): string {
  const mimeMap: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
  };
  return mimeMap[mime.toLowerCase()] || 'png';
}

export async function saveImageFromUrl(url: string, filename?: string): Promise<SaveImageResult> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return saveImage(blob, filename);
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : '获取图片失败',
    };
  }
}
